import type { InterviewInviteSessionRubricScoreDto, SessionRubricFlagDto } from '@sara/api-client';
import { TONE_FILL, TONE_RING, RATING_BARS, scoreTone, type RatingTone } from './format';

/**
 * Fired flags are score-neutral, so their tone comes from the flag's category,
 * not the numeric score: negative = attention (warning), positive = success,
 * informational = neutral.
 */
const FLAG_TONE: Record<SessionRubricFlagDto['category'], RatingTone> = {
  NEGATIVE: 'adequate',
  POSITIVE: 'strong',
  INFORMATIONAL: 'neutral',
};

const FLAG_CATEGORY_LABEL: Record<SessionRubricFlagDto['category'], string> = {
  NEGATIVE: 'negative',
  POSITIVE: 'positive',
  INFORMATIONAL: 'info',
};

const FLAG_EDGE: Record<SessionRubricFlagDto['category'], string> = {
  NEGATIVE: 'border-l-2 border-l-warning/60',
  POSITIVE: 'border-l-2 border-l-success/60',
  INFORMATIONAL: 'border-l-2 border-l-muted-foreground/30',
};

function FiredFlag({ flag }: { flag: SessionRubricFlagDto }) {
  const tone = FLAG_TONE[flag.category];
  return (
    <article
      className={
        'space-y-2 rounded-xl border border-border bg-card p-4 ' + FLAG_EDGE[flag.category]
      }
      data-testid={`fired-flag-${flag.key}`}
    >
      <header className="flex items-start justify-between gap-3">
        {/* The stable key, monospace + uppercase — the flag's identity, prototype-style. */}
        <h4
          className="font-mono text-[12.5px] font-semibold uppercase tracking-wide text-foreground"
          title={flag.name}
        >
          {flag.key}
        </h4>
        <span
          className={
            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
            TONE_FILL[tone]
          }
        >
          <span className={'h-1.5 w-1.5 rounded-full ' + TONE_RING[tone]} aria-hidden />
          {FLAG_CATEGORY_LABEL[flag.category]}
        </span>
      </header>

      {flag.evidence.map((e, i) => (
        <blockquote
          key={i}
          className="border-l-2 border-primary pl-3 text-[13px] italic text-muted-foreground"
        >
          &ldquo;{e.quote}&rdquo;
        </blockquote>
      ))}

      {flag.note && (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{flag.note}</p>
      )}
    </article>
  );
}

export function ScoresSection({ score }: { score: InterviewInviteSessionRubricScoreDto }) {
  const overallTone = scoreTone(score.overallScore);

  return (
    <section className="space-y-4">
      <article className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
        <span
          className={
            'inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-3xl font-semibold tabular-nums ring-1 ring-inset ' +
            TONE_FILL[overallTone]
          }
        >
          {score.overallScore.toFixed(1)}
        </span>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight text-foreground">
            {score.bandLabel}
          </h3>
          <p className="mt-0.5 text-[12.5px] text-muted-foreground">
            Scored against {score.rubricName} · v{score.rubricVersion}
          </p>
        </div>
      </article>

      {/* Fired flags read first — they're the "look at this" layer above the score detail. */}
      {(score.flags ?? []).length > 0 && (
        <div className="space-y-2 pt-2">
          <h4 className="text-[11px] font-medium uppercase tracking-[0.08em] tabular-nums text-muted-foreground">
            Flags · {score.flags.length}
          </h4>
          <p className="text-[12px] text-muted-foreground/80">
            Conditions detected in the call, with evidence. Flags never change the score.
          </p>
        </div>
      )}
      {(score.flags ?? []).map((flag) => (
        <FiredFlag key={flag.key} flag={flag} />
      ))}

      <div className="space-y-2 pt-2">
        <h4 className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Dimension scores
        </h4>
        <p className="text-[12px] text-muted-foreground/80">
          Each graded 1–5 against its anchors; weights combine into the overall score above.
        </p>
      </div>
      {score.dimensions.map((d) => {
        const tone = scoreTone(d.score);
        const filled = RATING_BARS[tone];
        return (
          <article key={d.key} className="space-y-3 rounded-xl border border-border bg-card p-4">
            <header className="flex items-start justify-between gap-3">
              <h4 className="text-[14px] font-semibold leading-tight text-foreground">
                {d.name}{' '}
                <span className="text-[12px] font-normal text-muted-foreground">· {d.weight}%</span>
              </h4>
              <span
                className={
                  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                  TONE_FILL[tone]
                }
              >
                <span className={'h-1.5 w-1.5 rounded-full ' + TONE_RING[tone]} aria-hidden />
                {d.score.toFixed(1)}
              </span>
            </header>

            <div className="flex h-1 gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={
                    'flex-1 rounded-full ' + (i < filled ? TONE_RING[tone] : 'bg-secondary')
                  }
                  aria-hidden
                />
              ))}
            </div>

            <p className="text-[13px] leading-relaxed text-muted-foreground">{d.rationale}</p>

            {d.evidence.map((e, i) => (
              <blockquote
                key={i}
                className="border-l-2 border-primary pl-3 text-[13px] italic text-muted-foreground"
              >
                &ldquo;{e.quote}&rdquo;
              </blockquote>
            ))}
          </article>
        );
      })}
    </section>
  );
}
